import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { selectIncidentGuid, useAppSelector } from 'slices/commonSelectors';
import { parseError } from 'utils/parseTypedErrorMessage';
import {
  CreateIncidentStatusSummaryRequestBody,
  IncidentStatusSummaryDto,
  UpdateIncidentStatusSummaryRequestBody,
} from 'types/iapBaseTypes';

export const keys = {
  incidentStatusSummary: 'incidentStatusSummary',
} as const;

export const useIncidentStatusSummaries = (incidentId: string) => {
  return useQuery({
    queryKey: [keys.incidentStatusSummary, incidentId],
    queryFn: async () => {
      const response = await axios.get<IncidentStatusSummaryDto[]>(
        `${config.apiGateway.ics_incident_status_summary}/${incidentId}`
      );
      return response.data;
    },
    enabled: !!incidentId,
  });
};

export const useCreateIncidentStatusSummary = () => {
  const queryClient = useQueryClient();
  const incidentId = useAppSelector(selectIncidentGuid);

  return useMutation({
    mutationFn: async (
      data: Omit<CreateIncidentStatusSummaryRequestBody, 'incidentId'>
    ) => {
      const payload: CreateIncidentStatusSummaryRequestBody = {
        ...data,
        incidentId: incidentId!,
      };

      const response = await axios.post<IncidentStatusSummaryDto>(
        config.apiGateway.ics_incident_status_summary,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      if (incidentId) {
        queryClient.invalidateQueries({
          queryKey: [keys.incidentStatusSummary, incidentId],
        });
      }
    },
    onError: (error: unknown) => {
      toast.error(
        parseError(error) ?? 'Failed to create incident status summary'
      );
    },
  });
};

export const useUpdateIncidentStatusSummary = () => {
  const queryClient = useQueryClient();
  const incidentId = useAppSelector(selectIncidentGuid);

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateIncidentStatusSummaryRequestBody;
    }) => {
      const response = await axios.put<IncidentStatusSummaryDto>(
        `${config.apiGateway.ics_incident_status_summary}/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      if (incidentId) {
        queryClient.invalidateQueries({
          queryKey: [keys.incidentStatusSummary, incidentId],
        });
      }
    },
    onError: (error: unknown) => {
      toast.error(
        parseError(error) ?? 'Failed to update incident status summary'
      );
    },
  });
};

export const useUpsertIncidentStatusSummary = (id?: string) => {
  const { mutate: createIncidentStatusSummary } =
    useCreateIncidentStatusSummary();

  const { mutate: updateIncidentStatusSummary } =
    useUpdateIncidentStatusSummary();

  if (id) {
    return (data: UpdateIncidentStatusSummaryRequestBody) =>
      updateIncidentStatusSummary({ id, data });
  } else {
    return createIncidentStatusSummary;
  }
};

export const useDeleteIncidentStatusSummary = () => {
  const queryClient = useQueryClient();
  const incidentId = useAppSelector(selectIncidentGuid);

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `${config.apiGateway.ics_incident_status_summary}/${id}`
      );
      return response.data;
    },
    onSuccess: () => {
      if (incidentId) {
        queryClient.invalidateQueries({
          queryKey: [keys.incidentStatusSummary, incidentId],
        });
      }
      toast.success('Incident status summary deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(
        parseError(error) ?? 'Failed to delete incident status summary'
      );
    },
  });
};
